// Segment payload hygiene — WL-072 / EW-013.
//
// The Wheel/Segment editor tags the currently-open segment with a transient UI
// flag `selected` (SegmentBuilder/index.jsx sets `selected: position === ...`
// on every segment-open, and clears it on close). That flag is a pure
// view-state concern — it is recomputed client-side on every load and is never
// read back from the server. Historically `generatePayload` spread the whole
// segment (`...restSegment`) into the save payload, so `selected` rode along.
//
// Consequences: (1) the change-detector saw a diff on every segment click
// (each open/close toggles `selected`), producing spurious "unsaved changes"
// and autosave churn; (2) payload noise persisted a UI-only flag server-side.
//
// This module extracts a single, pure, unit-tested sanitizer that strips the
// transient fields from a segment before it is serialized into the save
// payload. It removes ONLY view-transient keys — no stored styling, nodes,
// settings, or ids are touched — so it cannot lose user data.

// Keys that are pure editor view-state and must never be persisted.
//   selected      — which segment is currently open in the editor
//   initialNode   — a hydration scratch field (already dropped by the old
//                   destructure; kept here so the sanitizer is the single
//                   source of truth)
//   _labelDisplay — a D3-render-only annotation (createPie.js's
//                   resolveLabelOverlaps writes "full"/"dot"/"none" directly
//                   onto the segment datum to degrade overlapping outer label
//                   pills). It is purely a function of viewport size at draw
//                   time, never user content, so it must never reach the save
//                   payload (#builder-fresh-unsaved round 4).
export const TRANSIENT_SEGMENT_FIELDS = [
  "selected",
  "initialNode",
  "_labelDisplay",
];

/**
 * Return a shallow copy of `segment` with all transient editor-only fields
 * removed. Non-object input is returned unchanged.
 * @param {object} segment
 * @returns {object}
 */
export function stripTransientSegmentFields(segment) {
  if (!segment || typeof segment !== "object") return segment;
  const clean = { ...segment };
  for (const key of TRANSIENT_SEGMENT_FIELDS) {
    delete clean[key];
  }
  return clean;
}

// ---------------------------------------------------------------------------
// Blob-src persist guard — QA-12.
//
// A persisted media src MUST be a durable, shareable URL (S3/CDN https). A
// `blob:` object-URL (URL.createObjectURL) is scoped to the ONE browser
// tab/session that created it — it cannot resolve for any other user, tab, or
// even a reload of the same tab once the object is GC'd. Persisting a blob: src
// to the segment `nodes` JSON therefore silently CORRUPTS the media: the block
// plays for the author's live tab and no one else.
//
// Real incident (QA-12): an AI-built wheel (prod 869469) saved 8 VideoWidget
// nodes whose props.src were all `blob:https://web.iyuga.net/<uuid>` — the
// videos played for nobody, and no permanent asset existed to recover. The live
// builder upload paths (UploadMedia / MediaRecorder -> handleFileUpload -> S3
// presigned) already upload-then-swap to a real URL, so this guard is the
// LAST-LINE, path-agnostic backstop: it runs on the save payload right before
// persist and blanks any node whose media src is a blob:, so a blob can NEVER
// reach the saved JSON regardless of which upstream add/generate path (present,
// legacy, or future) produced it.
//
// Blanking (not dropping the node) is deliberate: a blob src is already
// worthless (unrecoverable), so nothing recoverable is lost, and the block stays
// in place showing its clean empty/upload placeholder instead of a broken
// player. Any stuck `isLoading` on the same node is cleared so the placeholder
// renders rather than a permanent spinner.

export const BLOB_URL_PREFIX = "blob:";

// craft.js node props that hold a media URL. Any of these carrying a blob: value
// is unpersistable and gets blanked.
const MEDIA_URL_PROPS = ["src", "thumbnail", "poster"];

// Exported so any render-side consumer (e.g. the Video block, which must
// treat an already-persisted blob: src as dead rather than feed it to a
// player and show an error — see BUG-editwheel-0bcd60) shares this ONE
// definition instead of re-deriving the "blob:" prefix check.
export const isBlobUrl = (value) =>
  typeof value === "string" && value.startsWith(BLOB_URL_PREFIX);

/**
 * Sanitize a craft.js node map for persistence by blanking any media prop
 * (src / thumbnail / poster) whose value is a `blob:` object-URL.
 *
 * Accepts the node map as a plain object OR its JSON string, and returns the
 * SAME kind it was given (object in -> object out; string in -> string out) so
 * it drops in wherever the pre-persist code has the nodes. When nothing needs
 * changing it returns the ORIGINAL reference untouched, so a blob-free save is
 * byte-identical to before this guard existed.
 *
 * @param {object|string} nodes craft node map or its JSON string
 * @returns {object|string}
 */
export function stripBlobMediaSrcs(nodes) {
  const wasString = typeof nodes === "string";

  let map = nodes;
  if (wasString) {
    try {
      map = JSON.parse(nodes);
    } catch (e) {
      // Not valid JSON — leave it exactly as-is (never throw from the save path).
      return nodes;
    }
  }

  if (!map || typeof map !== "object") return nodes;

  let mutated = false;
  const out = {};

  for (const [id, node] of Object.entries(map)) {
    if (
      node &&
      typeof node === "object" &&
      node.props &&
      typeof node.props === "object"
    ) {
      let props = node.props;
      let nodeMutated = false;

      for (const key of MEDIA_URL_PROPS) {
        if (isBlobUrl(props[key])) {
          if (!nodeMutated) {
            props = { ...props };
            nodeMutated = true;
          }
          props[key] = "";
        }
      }

      if (nodeMutated) {
        // Clear a stuck loading flag so the block shows its empty state, not a
        // permanent spinner, once its blob src is gone.
        if ("isLoading" in props) props.isLoading = false;
        out[id] = { ...node, props };
        mutated = true;
        continue;
      }
    }
    out[id] = node;
  }

  if (!mutated) return nodes; // byte-identical: return the original reference

  return wasString ? JSON.stringify(out) : out;
}

// ---------------------------------------------------------------------------
// serializeSegmentNodes — the single, exactly-once JSON-encode chokepoint for
// a segment's `nodes` in the save payload. P5-R3.
//
// A segment's `nodes` is an OBJECT the moment it is hydrated from the initial
// wheel fetch (SegmentBuilder JSON.parses the server's string once on load),
// but becomes a STRING the instant the user edits ANYTHING on that segment's
// canvas: craft.js's onNodesChange -> query.serialize() already returns a
// JSON string, and SegmentBuilder stores that string as-is in Redux (see
// pages/SegmentBuilder/index.jsx's `editorState` sync). Both shapes are valid
// — stripBlobMediaSrcs above deliberately returns whichever kind it was given
// — but a caller that unconditionally wraps the result in `JSON.stringify()`
// (as generatePayload used to) double-encodes the STRING case: the payload's
// `nodes` value becomes a JSON string OF a JSON string.
//
// craft.js's own deserialize() is forgiving (it JSON.parses whatever string
// it receives, so the canvas keeps rendering after a reload), which is why
// this stayed hidden — but every OTHER consumer of a segment's nodes (char
// count, media-url extraction for delete-refcounting, the public-view
// renderer's own hydration) receives a bare string instead of a node map and
// silently finds nothing. It disproportionately hits the block a user JUST
// edited, because that is precisely the segment whose `nodes` is a string at
// save time — e.g. the Video "Embed URL" block: paste a URL, Confirm applies
// it to the canvas instantly, Save serializes it as a double-encoded string,
// and the persisted JSON has no discoverable `props.src` at all.
//
// @param {object|string} nodes craft.js node map, or its JSON string
// @returns {string} the nodes, blob-sanitized, encoded EXACTLY once
export function serializeSegmentNodes(nodes) {
  const cleaned = stripBlobMediaSrcs(nodes);
  return typeof cleaned === "string" ? cleaned : JSON.stringify(cleaned);
}
